import type { CrudFilter, CrudSorting, DataProvider } from "@refinedev/core";

import { FhirClient, FhirError } from "@/fhir";
import type { Bundle, BundleEntry, Resource } from "@/fhir/types";
import { FhirResourceType, HttpStatusCode } from "@/utils";
import { IF_MATCH_HEADER } from "@/utils/fhir-constants";

import { getIdToken } from "../auth/token-provider";
import { fetchAllPages } from "./fetch-utils";

export const DEFAULT_PAGE_SIZE = 100;
export const ONE_MINUTE_STALE_TIME = 60 * 1000;
export const FIVE_MINUTE_STALE_TIME = 5 * 60 * 1000;

interface ErrorWithStatus extends Error {
  status?: number;
  statusCode?: number;
}

/**
 * Enhance error object with status information for auth provider handling
 * This ensures that 401 and 410 errors are properly detected by the auth provider
 */
const enhanceErrorWithStatus = (error: unknown): void => {
  if (error && typeof error === "object") {
    // Handle FhirError
    if (error instanceof FhirError && error.statusCode) {
      if (!("status" in error) || !(error as ErrorWithStatus).status) {
        (error as ErrorWithStatus).status = error.statusCode;
      }
      if (!("statusCode" in error) || !(error as ErrorWithStatus).statusCode) {
        (error as ErrorWithStatus).statusCode = error.statusCode;
      }
      return;
    }

    if ("status" in error || "statusCode" in error) {
      return;
    }

    // Check if it's a 401 Unauthorized or 410 Gone response from the error message
    const errorMessage = (error as Error).message || "";
    if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
      (error as ErrorWithStatus).status = HttpStatusCode.UNAUTHORIZED;
    } else if (errorMessage.includes("410") || errorMessage.toLowerCase().includes("gone")) {
      (error as ErrorWithStatus).status = HttpStatusCode.GONE;
    }
  }
};

/**
 * Create a Refine.dev data provider using the FHIR R4 client
 *
 * This data provider translates Refine operations to FHIR REST API calls
 * and handles FHIR Bundle responses.
 *
 * @param fhirClient - The FHIR client instance to use for API calls
 * @returns A Refine.dev data provider
 */
export const createFhirDataProvider = (fhirClient: FhirClient): DataProvider =>
  ({
    getList: async ({ resource, pagination, filters, sorters, meta }) => {
      const searchParams: Record<string, string | number | (string | number)[]> = {};

      // Handle pagination — use meta.pageSize first, then pagination.pageSize,
      // otherwise fall back to DEFAULT_PAGE_SIZE (100)
      if (meta?.fetchAllPages) {
        searchParams._count = meta?.pageSize ?? DEFAULT_PAGE_SIZE;
      } else if (pagination && pagination.mode !== "off") {
        const pageSize = meta?.pageSize ?? pagination?.pageSize ?? DEFAULT_PAGE_SIZE;
        searchParams._count = pageSize;
        if (pagination.currentPage && pagination.currentPage > 1) {
          searchParams._offset = (pagination.currentPage - 1) * pageSize;
        }
      }

      // Handle sorting
      if (sorters && sorters.length > 0) {
        const sortParams = sorters.map(
          (sorter: CrudSorting[number]) => `${sorter.order === "desc" ? "-" : ""}${sorter.field}`
        );
        searchParams._sort = sortParams.join(",");
      }

      // Handle filters
      if (filters) {
        filters.forEach((filter: CrudFilter) => {
          if (
            filter.operator === "eq" &&
            (typeof filter.value === "boolean" || typeof filter.value === "number" || filter.value?.length > 0)
          ) {
            searchParams[filter.field] = filter.value;
          } else if (filter.operator === "in" && filter.value?.length > 0) {
            // For 'in' operator, value should be comma-separated string
            searchParams[filter.field] = filter.value;
          } else if (filter.operator === "contains" && filter.value?.length > 0) {
            searchParams[`${filter.field}:contains`] = filter.value;
          } else if (
            filter.operator === "between" &&
            Array.isArray(filter.value) &&
            filter.value.length === 2 &&
            filter.value[0] &&
            filter.value[1]
          ) {
            // FHIR date/number ranges use two prefixed values on the same param: ge<start>&le<end>
            const [start, end] = filter.value;
            searchParams[filter.field] = [`ge${start}`, `le${end}`];
          }
        });
      }

      // Add custom query parameters from meta
      if (meta?.customQueryParameters && Array.isArray(meta.customQueryParameters)) {
        meta.customQueryParameters.forEach((param: string) => {
          const [key, value] = param.split("=");
          if (key && value) {
            searchParams[key] = value;
          }
        });
      }
      // Handle _elements (field filtering to reduce payload size)
      if (meta?._elements) {
        const elements = Array.isArray(meta._elements) ? meta._elements : [meta._elements];
        if (elements.length) {
          searchParams._elements = elements.join(",");
        }
      }

      // Handle _include / _revinclude
      if (meta?._include) {
        const includes = Array.isArray(meta._include) ? meta._include : [meta._include];
        if (includes.length) {
          searchParams._include = includes.join(",");
        }
      }

      if (meta?._revinclude) {
        const revincludes = Array.isArray(meta._revinclude) ? meta._revinclude : [meta._revinclude];
        if (revincludes.length) {
          searchParams._revinclude = revincludes.join(",");
        }
      }
      try {
        // Handle fetchAllPages - follow FHIR pagination links to get all results
        if (meta?.fetchAllPages) {
          const urlSearchParams = new URLSearchParams();
          Object.entries(searchParams).forEach(([k, v]) => {
            if (Array.isArray(v)) {
              v.forEach(item => urlSearchParams.append(k, String(item)));
            } else {
              urlSearchParams.append(k, String(v));
            }
          });
          const queryString = urlSearchParams.toString();
          const url = `${fhirClient.getBaseUrl()}/${resource}${queryString ? `?${queryString}` : ""}`;

          const onProgress =
            typeof meta?.onProgress === "function" ? (meta.onProgress as (entries: unknown[]) => void) : undefined;
          const { data: entries } = await fetchAllPages(url, {}, meta?.maxEntries, onProgress);
          const data = (entries as BundleEntry[])
            .map(entry => entry.resource)
            .filter((r: Resource | undefined): r is Resource => r !== undefined);

          return { data, total: data.length };
        }

        // Standard single-page fetch
        const bundle = await fhirClient.search<Resource>(resource, searchParams);

        // Extract resources from bundle entries
        const data = (bundle.entry || [])
          .map((entry: BundleEntry) => entry.resource)
          .filter((r: Resource | undefined): r is Resource => r !== undefined);

        // Get total count from the bundle
        const total = bundle.total || data.length;

        return { data, total };
      } catch (error) {
        console.error("Error fetching resources:", error);

        enhanceErrorWithStatus(error);
        throw error;
      }
    },

    getOne: async ({ resource, id }) => {
      try {
        const data = await fhirClient.read(resource, id.toString());
        return { data };
      } catch (error) {
        console.error("Error fetching resource:", error);

        enhanceErrorWithStatus(error);
        throw error;
      }
    },

    getMany: async ({ resource, ids }) => {
      try {
        const results = await Promise.all(ids.map(id => fhirClient.read<Resource>(resource, id.toString())));
        return { data: results };
      } catch (error) {
        console.error("Error fetching resources:", error);

        enhanceErrorWithStatus(error);
        throw error;
      }
    },

    update: async ({ resource, id, variables, meta }) => {
      try {
        // Auto-add If-Match header for concurrency control when meta.versionId
        // is provided. Callers opt in by passing meta: { versionId: "..." }.
        // A header already present in meta.headers takes priority.
        const headers = { ...meta?.headers };
        if (!headers[IF_MATCH_HEADER] && meta?.versionId) {
          headers[IF_MATCH_HEADER] = `W/"${meta.versionId}"`;
        }

        let data: Resource;

        if (meta?.isPatch) {
          // Handle PATCH operations - convert variables to patch operations
          const patchOps = Array.isArray(variables)
            ? variables
            : [{ op: "replace" as const, path: "/", value: variables }];
          data = await fhirClient.patch(resource, id.toString(), patchOps, headers);
        } else {
          // Handle PUT operations (full update)
          data = await fhirClient.update(
            {
              ...variables,
              resourceType: resource,
              id: id.toString(),
            } as Resource,
            headers
          );
        }

        return { data };
      } catch (error) {
        console.error("Error updating resource:", error);

        enhanceErrorWithStatus(error);
        throw error;
      }
    },

    create: async ({ resource, variables, meta }) => {
      try {
        if (resource === FhirResourceType.Bundle) {
          // Handle batch/transaction bundles
          const data = await fhirClient.batch(variables as Bundle, meta?.headers);
          return { data };
        } else {
          // Handle regular resource creation
          const data = await fhirClient.create(
            {
              ...variables,
              resourceType: resource,
            } as Resource,
            meta?.headers
          );

          return { data };
        }
      } catch (error) {
        console.error("Error creating resource:", error);

        enhanceErrorWithStatus(error);
        throw error;
      }
    },

    deleteOne: async ({ resource, id }) => {
      try {
        await fhirClient.delete(resource, id.toString());
        return { data: { id } as Resource };
      } catch (error) {
        console.error("Error deleting resource:", error);

        enhanceErrorWithStatus(error);
        throw error;
      }
    },

    getApiUrl: () => {
      return fhirClient.getBaseUrl();
    },

    // Batch/Transaction Bundle submission — a custom extension to DataProvider.
    batch: async <T extends Resource>(bundle: Bundle<T>, extraHeaders?: Record<string, string>): Promise<Bundle<T>> => {
      try {
        return await fhirClient.batch(bundle, extraHeaders);
      } catch (error) {
        console.error("Error submitting bundle:", error);
        enhanceErrorWithStatus(error);
        throw error;
      }
    },

    // Custom method for direct API calls
    custom: async ({ url, method, headers, payload }) => {
      try {
        const response = await fetch(url, {
          method: method || "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getIdToken()}`,
            ...headers,
          },
          body: payload ? JSON.stringify(payload) : undefined,
          cache: "no-store",
        });

        // Check for 401 Unauthorized or 410 Gone response
        if (response.status === HttpStatusCode.UNAUTHORIZED) {
          const error = new Error("Unauthorized");
          (error as ErrorWithStatus).status = HttpStatusCode.UNAUTHORIZED;
          throw error;
        } else if (response.status === HttpStatusCode.GONE) {
          const error = new Error("Resource no longer available");
          (error as ErrorWithStatus).status = HttpStatusCode.GONE;
          throw error;
        } else if (!response.ok) {
          const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
          (error as ErrorWithStatus).status = response.status;
          throw error;
        }

        const data = await response.json();

        return { data };
      } catch (error) {
        console.error("Error with custom request:", error);

        enhanceErrorWithStatus(error);
        throw error;
      }
    },
  }) as DataProvider;
